/**
 * Category utility functions for courses and books
 *
 * @format
 */

// Course category mappings (cat_id to display name)
export const COURSE_CATEGORY_MAP = {
  // ICAG categories
  "ICAG - Level 1": "ICAG - Level 1",
  "ICAG - Level 2": "ICAG - Level 2",
  "ICAG - Level 3": "ICAG - Level 3",

  // CITG categories
  "CITG - Level 1": "CITG - Level 1",
  "CITG - Level 2": "CITG - Level 2",
  "CITG - Level 3": "CITG - Level 3",
  "CITG - Professional Level": "CITG - Professional Level",

  // Other categories
  "Corporate Training": "Corporate Training",
  "Digital Marketing and Business Growth":
    "Digital Marketing and Business Growth",

  // Custom categories (fallback)
  custom: "Custom Category",
};

// Book categories with subcategories
export const BOOK_CATEGORIES = {
  ICAG: ["Level 1", "Level 2", "Level 3", "Professional Level"],
  CITG: ["Level 1", "Level 2", "Level 3", "Professional Level"],
  "Corporate Training": [
    "Leadership",
    "Communication",
    "Project Management",
    "Sales & Marketing",
    "Finance & Accounting",
    "Human Resources",
    "Technology",
    "Operations",
  ],
  "Digital Marketing": [
    "Social Media Marketing",
    "Content Marketing",
    "SEO & SEM",
    "Email Marketing",
    "Analytics & Data",
    "E-commerce Marketing",
    "Influencer Marketing",
    "Video Marketing",
  ],
};

// Function to get course category display name
export const getCourseCategoryDisplayName = (catId) => {
  if (!catId) return "";

  // Check if it's a known category
  if (COURSE_CATEGORY_MAP[catId]) {
    return COURSE_CATEGORY_MAP[catId];
  }

  // If it's a custom category, format it nicely
  if (catId.startsWith("custom_")) {
    return catId.replace("custom_", "Custom: ");
  }

  // Return as is if no mapping found
  return catId;
};

// Function to get book category options for dropdown
export const getBookCategoryOptions = () => {
  return Object.keys(BOOK_CATEGORIES).map((category) => ({
    id: category,
    name: category,
  }));
};

// Function to get subcategory options for a specific category
export const getBookSubcategoryOptions = (category) => {
  if (!category || !BOOK_CATEGORIES[category]) {
    return [];
  }
  return BOOK_CATEGORIES[category].map((subcategory) => ({
    id: subcategory,
    name: subcategory,
  }));
};

// Function to get all subcategories across all categories
export const getAllBookSubcategories = () => {
  const allSubcategories = [];
  Object.values(BOOK_CATEGORIES).forEach((subcategories) => {
    allSubcategories.push(...subcategories);
  });
  return allSubcategories;
};

// Function to check if a category is custom
export const isCustomCategory = (category) => {
  return !Object.keys(BOOK_CATEGORIES).includes(category);
};

// Function to check if a subcategory is custom
export const isCustomSubcategory = (category, subcategory) => {
  if (!category || !subcategory) return false;
  if (isCustomCategory(category)) return true;
  return !BOOK_CATEGORIES[category]?.includes(subcategory);
};

// Function to get category options including custom option
export const getBookCategoryOptionsWithCustom = () => {
  const predefinedOptions = getBookCategoryOptions();
  return [
    ...predefinedOptions,
    { id: "custom", name: "➕ Add Custom Category" },
  ];
};

// Function to get subcategory options including custom option
export const getBookSubcategoryOptionsWithCustom = (category) => {
  if (!category || category === "custom") return [];

  const predefinedOptions = getBookSubcategoryOptions(category);
  return [
    ...predefinedOptions,
    { id: "custom", name: "➕ Add Custom Subcategory" },
  ];
};

// Function to check if a category is predefined
export const isPredefinedCategory = (category) => {
  return Object.keys(BOOK_CATEGORIES).includes(category);
};

// Function to get all course category options (for filtering)
export const getCourseCategoryOptions = () => {
  return Object.keys(COURSE_CATEGORY_MAP).map((catId) => ({
    id: catId,
    name: COURSE_CATEGORY_MAP[catId],
  }));
};
